import React, { useState, useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { IconWand, IconX } from '@tabler/icons-react';

import WizardStepIndicator from './WizardStepIndicator';
import WizardFooter from './WizardFooter';
import IdealizationStep from './steps/IdealizationStep';
import PlanningStep from './steps/PlanningStep';
import KnowledgeBaseStep from './steps/KnowledgeBaseStep';
import CreationStep from './steps/CreationStep';

import './AgentCreationWizard.css';

const INITIAL_STATE = {
  currentStep: 1,
  businessType: null,
  assistantGender: null,
  businessDescription: '',
  idealizationSummary: null,
  userPlan: null,
  technicalPlan: null,
  knowledgeOption: null,
  knowledgeFileKey: null,
  knowledgeDiagnostic: null,
  createdAgent: null,
  pdfGuideUrl: null,
  isLoading: false,
  // Internal flags
  _idealizationApproved: false,
  _idealizationIteration: 0,
  _planApproved: false,
  _planIteration: 0,
};

const STEP_SUBTITLES = {
  1: 'Conte-nos sobre seu negócio para criar o agente ideal',
  2: 'Revise o plano personalizado para o seu agente',
  3: 'Adicione conhecimento para respostas mais precisas',
  4: 'Finalize a criação do seu agente inteligente',
};

const AgentCreationWizard = ({ show, onComplete, onSkip }) => {
  const [wizardState, setWizardState] = useState(INITIAL_STATE);
  const { currentStep, technicalPlan, createdAgent } = wizardState;

  // Should we skip knowledge step?
  const skipKnowledge = useMemo(() => {
    return technicalPlan ? !technicalPlan.needs_knowledge_base : false;
  }, [technicalPlan]);

  // Can we advance to next step?
  const canGoNext = useMemo(() => {
    switch (currentStep) {
      case 1:
        return wizardState._idealizationApproved && !!wizardState.idealizationSummary;
      case 2:
        return wizardState._planApproved && !!wizardState.technicalPlan;
      case 3:
        return wizardState.knowledgeOption === 'later' ||
          (wizardState.knowledgeOption === 'upload' && !!wizardState.knowledgeFileKey);
      case 4:
        return true;
      default:
        return false;
    }
  }, [currentStep, wizardState]);

  const handleNext = useCallback(() => {
    if (currentStep === 4) return;

    let nextStep = currentStep + 1;

    // Skip knowledge step if not needed
    if (nextStep === 3 && skipKnowledge) {
      nextStep = 4;
    }

    setWizardState((prev) => ({ ...prev, currentStep: nextStep }));
  }, [currentStep, skipKnowledge]);

  const handleBack = useCallback(() => {
    let prevStep = currentStep - 1;

    // Skip knowledge step backwards if not needed
    if (prevStep === 3 && skipKnowledge) {
      prevStep = 2;
    }

    if (prevStep >= 1) {
      setWizardState((prev) => ({ ...prev, currentStep: prevStep }));
    }
  }, [currentStep, skipKnowledge]);

  const handleComplete = useCallback((agent) => {
    onComplete(agent);
    setWizardState(INITIAL_STATE);
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    onSkip();
    setWizardState(INITIAL_STATE);
  }, [onSkip]);

  // Hide footer on success/creating states — CreationStep has its own CTA
  const showFooter = !(createdAgent || wizardState.isLoading);

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      className="awc-modal"
      dialogClassName="awc-modal__dialog"
    >
      <Modal.Body className="p-0 d-flex flex-column" style={{ maxHeight: '85vh' }}>
        {/* Header with gradient */}
        <div className="awc-header">
          <div className="awc-header__icon">
            <IconWand size={24} />
          </div>
          <div>
            <h2 className="awc-header__title">Assistente de Criação</h2>
            <p className="awc-header__subtitle">{STEP_SUBTITLES[currentStep]}</p>
          </div>
          <button
            className="awc-header__close"
            onClick={handleSkip}
            aria-label="Fechar assistente"
          >
            <IconX size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <WizardStepIndicator
          currentStep={currentStep}
          skipKnowledge={skipKnowledge}
        />

        {/* Scrollable content */}
        <div className="awc-body">
          <div className="awc-body__content">
            {currentStep === 1 && (
              <IdealizationStep
                wizardState={wizardState}
                setWizardState={setWizardState}
              />
            )}
            {currentStep === 2 && (
              <PlanningStep
                wizardState={wizardState}
                setWizardState={setWizardState}
              />
            )}
            {currentStep === 3 && (
              <KnowledgeBaseStep
                wizardState={wizardState}
                setWizardState={setWizardState}
              />
            )}
            {currentStep === 4 && (
              <CreationStep
                wizardState={wizardState}
                setWizardState={setWizardState}
                onComplete={handleComplete}
              />
            )}
          </div>
        </div>

        {/* Fixed footer */}
        {showFooter && (
          <WizardFooter
            currentStep={currentStep}
            onBack={handleBack}
            onNext={handleNext}
            onSkip={handleSkip}
            canGoNext={canGoNext}
            isLoading={wizardState.isLoading}
            skipKnowledge={skipKnowledge}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AgentCreationWizard;
